import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '~/api/errors';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

import { useRun } from '../hooks/use-run';
import {
  PRE_SCORE_PAGE_SIZE,
  runPreScoresQuery,
  runScorecardQuery,
  useRunScorecard,
} from '../hooks/use-run-content';
import { useFunnelProducts } from '../hooks/use-run-funnel';
import { useRunCostEstimate } from '../hooks/use-run-ledger';
import { useRunLineageSummary } from '../hooks/use-run-lineage';
import { useRunProgress } from '../hooks/use-run-progress';
import { useRunTitle } from '../hooks/use-run-title';
import { catalogLabel } from '../utils/catalog-labels';
import { hasCoverage } from '../utils/content-results';
import { NavOriginCtx, useNavContext } from '../utils/nav-context';
import { revealSectionById } from '../utils/reveal-section';
import { RunBreadcrumbTailCtx, type RunBreadcrumbTail } from '../utils/run-breadcrumb-tail';
import { formatCount } from '../utils/run-format';
import {
  activeRunTab,
  reachedContent,
  RUN_TABS,
  RUN_TABS_ANCHOR_ID,
  visibleTabs,
} from '../utils/run-tabs';
import { StageDestinationCtx } from '../utils/stage-destination-context';

import type { Crumb } from './page-breadcrumbs';
import { BackToOriginChip } from './page-breadcrumbs';
import { RunHeader } from './run-header';

/** Tabs that lay out their own cards; every other tab sits in the shell's one card. */
const SELF_CARDED_TABS: ReadonlySet<string> = new Set([
  'overview',
  'products',
  'insights',
  'delivery',
  'activity',
  'cost',
]);

interface Props {
  runId: string;
}

/**
 * Frame for every `/catalog-enrichment/runs/<runId>/*` page: the header band (breadcrumb, name,
 * state, the one primary action, facts), then the contextual tab strip — a tab appears only once
 * it means something (hidden, never disabled), an open gate badges Products in amber. Everything
 * page-specific (outcome, open actions, cost gate) lives on its tab, not above the strip. Below
 * 768px the strip becomes a `Select`.
 */
export function RunShell({ runId }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const { pathname, href, search } = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const runQuery = useRun(runId);
  const progressQuery = useRunProgress(runId);
  const progress = progressQuery.data;
  const pendingGate = progress?.pendingGate ?? null;
  const queryClient = useQueryClient();
  const preScoreGateOpen = pendingGate?.stage === 'PRE_ENRICH_SCORE';
  // A score gate is waiting: warm the decision panel's two slow reads (≈2 s products + ≈1 s
  // scores) while the user is still on another tab, so opening Products shows the table at once.
  useEffect(() => {
    if (!preScoreGateOpen) return;
    void queryClient.prefetchQuery(runPreScoresQuery(runId, 1, PRE_SCORE_PAGE_SIZE));
    void queryClient.prefetchQuery(runScorecardQuery(runId));
  }, [preScoreGateOpen, queryClient, runId]);
  // Cheap existence check only (same cache entry the Issues tab itself reads): the Issues tab
  // shows once this batch has any failed or still-flagged row to group by reason.
  const lineageSummary = useRunLineageSummary(runId);
  const hasIssues =
    (lineageSummary.data?.stages.reduce((sum, s) => sum + s.failed + s.revisit, 0) ?? 0) > 0;
  // Insights is always in the strip but enabled only once coverage exists — the SAME scorecard
  // read (and `hasCoverage` rule) the Insights page draws, fetched only after content generation
  // is part of the batch so earlier stages never pay for it.
  const contentReached = reachedContent(progress);
  const scorecardQuery = useRunScorecard(runId, contentReached);
  const insightsReady = hasCoverage(scorecardQuery.data);
  const gateBadge = t('catalog-enrichment-run:tabs.gateWaiting');
  // Hide, never disable (guide part 2 §2.2): Content appears once there is scored coverage.
  const hasEnrichment = !!progress?.stages.some((s) => s.stage === 'ENRICHMENT');
  const hasCost = !!useRunCostEstimate(runId, hasEnrichment).data;
  const visible = visibleTabs(progress, hasIssues, hasCost).filter(
    (tab) => tab.value !== 'insights' || insightsReady,
  );
  // R1-1 — this DESTINATION's own `from`/`back` (see `showOrigin` below).
  const nav = useNavContext();
  const active = activeRunTab(pathname, `/catalog-enrichment/runs/${runId}`);
  // A page whose owning tab isn't earned yet keeps that tab
  // in the strip while you're on it, so the highlighted tab and the breadcrumb always agree.
  const tabs = visible.some((tab) => tab.value === active)
    ? visible
    : RUN_TABS.filter((tab) => tab.value === active || visible.includes(tab));
  const { title, experimentId, experimentName } = useRunTitle(runQuery.data, progress);
  // The Products tab's count: the product table's own total (same cache entry as its tabs).
  const productMatrix = useFunnelProducts(runId, { page: 1, size: 1 }, true);
  const productTotal = productMatrix.data ? productMatrix.data.pagination.total : null;
  // Same resolved-connector value RunHeader shows ("Publish to X") — provided to the whole batch
  // subtree via context so every `stageLabel()` call under it picks it up without prop-drilling.
  const connector = progress?.connectorCode ?? runQuery.data?.connectorCode ?? null;
  const destination = connector ? catalogLabel(tCatalog, 'connector', connector) : null;
  // S8 — every cross-link rendered anywhere under this batch (a product cell, a flag badge, a
  // commerce-cell link…) picks up `from=batch:<runId>:<tab>` ambiently from here, never threaded
  // through as a prop; `backHref` is THIS page's own address, so the destination's "← Back to
  // <batch>" chip restores whatever tab/filters were open, not just the bare batch route.
  const navOrigin = useMemo(
    () => ({ origin: { kind: 'batch' as const, runId, tab: active }, backHref: href }),
    [runId, active, href],
  );

  /** Whether a tab carries the amber "Waiting" badge: only Review, while a decision is open. */
  const gateBadgesTab = (value: string): boolean =>
    value === 'review' && (!!pendingGate || !!progress?.costGate);

  // Any action that switches tab (Fix & retry options, a header link, …) brings the tab bar into
  // view so the reader sees what opened. A step deep link (`?fStage=` on Steps) is left to the
  // step detail, which reveals itself; the first render (page load) never scrolls.
  const stepInUrl = typeof (search as Record<string, unknown>).fStage === 'string';
  const shownTab = useRef(active);
  useEffect(() => {
    if (shownTab.current === active) return;
    shownTab.current = active;
    if (active === 'overview' && stepInUrl) return;
    revealSectionById(RUN_TABS_ANCHOR_ID);
  }, [active, stepInUrl]);

  /** Navigates to the tab's route; shared by the desktop tab strip and the mobile select. */
  const goToTab = (value: string) => {
    const tab = RUN_TABS.find((r) => r.value === value);
    // Each tab opens clean: Review's own params (`decision`, `lens`) carried into Products would
    // trip its "old decision link" redirect straight back to Review.
    if (tab) void navigate({ to: tab.to, params: { runId }, search: {} });
  };

  // R1-1 — this DESTINATION's own `from`/`back` (set by whatever cross-section link landed here,
  // e.g. a product's own history opening a specific batch attempt). Suppressed when the origin is
  // just this same batch (an ambient same-batch cross-link into a nested page would otherwise show
  // a prefix — and a back chip — that only repeats the batch the reader is already in).
  const showOrigin = !(nav.origin?.kind === 'batch' && nav.origin.runId === runId);
  // Design B — a same-batch link that carried its own `back` (a Products row's Compare › /
  // Journey ›) gets "← Back to Products", restoring that exact filtered list on return.
  const originTab = nav.origin?.kind === 'batch' ? nav.origin.tab : null;
  const sameBatchBack =
    !showOrigin && originTab && nav.back?.href && nav.back.href !== href
      ? {
          href: nav.back.href,
          labelKey: (RUN_TABS.find((tab) => tab.value === originTab) ?? RUN_TABS[0]).labelKey,
          // A link that named its exact spot (an Insights row's detail view) reads "← Back to <it>".
          label: nav.backLabel,
        }
      : null;
  const [tail, setTail] = useState<RunBreadcrumbTail | undefined>(undefined);
  const hasTail = !!tail && tail.crumbs.length > 0;
  const breadcrumbs: Crumb[] = [
    ...(showOrigin ? nav.originCrumbs : []),
    // From an experiment the trail is Experiments › <experiment> › <batch>: a "Batches" crumb in
    // the middle read like the experiment's tab but led to the global list.
    ...(showOrigin && nav.origin?.kind === 'experiment'
      ? []
      : [{ label: tCatalog('breadcrumbs.runs'), to: '/catalog-enrichment/runs' } as const]),
    // The trail ends at the batch on its own tabs (the tab bar names the tab); a nested page
    // (a product, an Insights detail) adds its own steps after a linked batch crumb.
    hasTail
      ? { label: title, to: '/catalog-enrichment/runs/$runId', params: { runId } }
      : { label: title },
    ...(hasTail ? tail.crumbs : []),
  ];

  // No such batch here (404 on both reads): one plain not-found state, never a header made of
  // fallbacks ("Untitled batch", "Not started yet") over two error lines.
  const notFound =
    runQuery.error instanceof ApiError && runQuery.error.statusCode === 404 && !progressQuery.data;
  if (notFound) {
    return (
      <div
        data-testid="catalog-enrichment-run-not-found"
        className="bg-card mx-auto mt-[12vh] flex max-w-[560px] flex-col items-center gap-3 rounded-xl border p-8 text-center"
      >
        <p className="text-foreground text-base font-semibold">
          {t('catalog-enrichment-run:notFound.title')}
        </p>
        <Button asChild variant="outline" size="sm" className="cursor-pointer">
          <Link
            to="/catalog-enrichment/runs"
            data-testid="catalog-enrichment-run-not-found-back-link"
          >
            {t('catalog-enrichment-run:notFound.back')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <RunBreadcrumbTailCtx.Provider value={setTail}>
      <NavOriginCtx.Provider value={navOrigin}>
        <StageDestinationCtx.Provider value={destination}>
          <div className="flex flex-col gap-4">
            <RunHeader
              runId={runId}
              run={runQuery.data}
              progress={progress}
              isLoading={runQuery.isLoading || progressQuery.isLoading}
              title={title}
              experimentId={experimentId}
              experimentName={experimentName}
              breadcrumbs={breadcrumbs}
              activeTab={active}
            />
            {/* R1-1 — the ONE "← Back to <origin>" chip for the whole batch subtree; a nested
            page no longer renders its own (same `showOrigin` rule as the breadcrumb prefix
            above). */}
            {showOrigin && nav.back ? <BackToOriginChip /> : null}
            {sameBatchBack ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground h-auto w-fit cursor-pointer gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                onClick={() => {
                  router.history.push(sameBatchBack.href);
                }}
              >
                <ArrowLeft aria-hidden="true" className="size-3.5 shrink-0" />
                <span className="truncate">
                  {tCatalog('navContext.backToTab', {
                    tab:
                      sameBatchBack.label ?? t(`catalog-enrichment-run:${sameBatchBack.labelKey}`),
                  })}
                </span>
              </Button>
            ) : null}
            {runQuery.isError && progressQuery.isError ? (
              <p role="alert" className="text-error text-sm">
                {t('catalog-enrichment-run:header.loadError')}
              </p>
            ) : null}

            {/* Tabs scroll with the page: pinned tabs sat on top of the tables' pinned headers.
                The id is the anchor actions above the tabs scroll to after opening a tab. */}
            {/* Fixed height, so tabs that appear once their reads land never shift the page. */}
            <div id={RUN_TABS_ANCHOR_ID} className="min-h-11 scroll-mt-4">
              {/* ≥768: the tab tray. <768: a Select, so the tabs never need horizontal scroll. */}
              <div className="hidden md:block">
                {/* Underline, like the Job page's page tabs: the open tab reads in primary with a bar
                    under it, so it can't be mistaken for a hovered one (the tray's white chip could). */}
                <SegmentedTabs
                  variant="underline"
                  value={active}
                  onChange={goToTab}
                  testId="catalog-enrichment-run-tabs-list"
                  options={tabs.map((tab) => {
                    // Any open gate badges Products (its decision panel); the APPROVAL gate also
                    // badges Summary, whose top band now carries the publish decision.
                    const waiting = gateBadgesTab(tab.value);
                    return {
                      value: tab.value,
                      label: t(`catalog-enrichment-run:${tab.labelKey}`),
                      // The batch's product count sits on its Products tab (one place for it).
                      ...(tab.value === 'products' && productTotal !== null
                        ? { badge: formatCount(productTotal), badgeTone: 'muted' as const }
                        : {}),
                      // A real link, so a tab opens in a new browser tab and its URL can be shared.
                      href: router.buildLocation({ to: tab.to, params: { runId }, search: {} })
                        .href,
                      // A waiting decision reads as a dot on its tab; the status band says the rest.
                      ...(waiting ? { dot: 'attention' as const, dotLabel: gateBadge } : {}),
                    };
                  })}
                  ariaLabel={t('catalog-enrichment-run:runTabs.ariaLabel')}
                />
              </div>
              <div className="md:hidden">
                <Select value={active} onValueChange={goToTab}>
                  <SelectTrigger
                    className="w-full cursor-pointer"
                    aria-label={t('catalog-enrichment-run:runTabs.ariaLabel')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tabs.map((tab) => (
                      <SelectItem key={tab.value} value={tab.value} className="cursor-pointer">
                        {t(`catalog-enrichment-run:${tab.labelKey}`)}
                        {gateBadgesTab(tab.value) ? ` · ${gateBadge}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {SELF_CARDED_TABS.has(active) ? (
              <Outlet />
            ) : (
              <div className="bg-card min-w-0 rounded-2xl border p-4 shadow-sm">
                <Outlet />
              </div>
            )}
          </div>
        </StageDestinationCtx.Provider>
      </NavOriginCtx.Provider>
    </RunBreadcrumbTailCtx.Provider>
  );
}
